<template>
  <div class="rule-builder-node">
    <div class="node-row d-flex align-items-center">
      <!-- Label -->
      <div class="value-label">
        {{ getOptionLabel(node.value) }}
      </div>

      <!-- Amount -->
      <div class="amount-wrapper">
        <input
          type="number"
          class="form-control"
          v-model.number="node.amount"
          :placeholder="placeholderText"
        >
        <div v-if="!node.amount && parentAmount" class="tiny-hint">
          Inherits ৳{{ parentAmount }}
        </div>
      </div>

      <!-- Actions -->
      <div class="node-actions d-flex align-items-center">
        <button
          v-if="!node.childField && level < 3"
          type="button"
          class="btn rb-btn-sub"
          @click="$emit('add-condition', node)"
        >
          + Sub
        </button>

        <button
          v-if="node.childField"
          type="button"
          class="btn rb-btn-delete"
          @click="$emit('remove-condition', node)"
        >
          <i class="fas fa-trash"></i>
        </button>

        <button
          v-if="node.childField"
          type="button"
          class="btn rb-btn-caret"
          @click="collapsed = !collapsed"
        >
          <i :class="['fas', collapsed ? 'fa-chevron-right' : 'fa-chevron-down']"></i>
        </button>
      </div>
    </div>

    <!-- Child Selector -->
    <div v-if="node.showFieldSelector" class="child-selector-box">
      <label class="rb-selector-label">Select Sub-Condition (Level {{ level + 1 }})</label>
      <div class="d-flex align-items-center">
        <select
          class="form-control form-control-sm mr-2"
          v-model="node.pendingChildField"
        >
          <option value="">-- Select Attribute --</option>
          <option
            v-for="field in availableFields"
            :key="field.id"
            :value="field.id"
          >
            {{ field.label }}
          </option>
        </select>
        <button type="button" class="btn rb-btn-add" @click="$emit('confirm-add', { node, level: level + 1 })">Add</button>
        <button type="button" class="btn rb-btn-cancel ml-1" @click="node.showFieldSelector = false">Cancel</button>
      </div>
    </div>

    <!-- Recursion -->
    <div v-if="node.childField && !collapsed" class="ctc-dashed-left">
      <div class="rb-field-caption">
        {{ getFieldLabel(node.childField) }}
      </div>
      <ctc-rule-builder-node 
        v-for="child in node.children" 
        :key="child.value"
        :node="child"
        :level="level + 1"
        :parent-amount="effectiveAmount"
        :used-fields="[...usedFields, node.childField]"
        :fields="fields"
        :current-field-id="node.childField"
        @add-condition="(childNode) => $emit('add-condition', childNode)"
        @remove-condition="(childNode) => $emit('remove-condition', childNode)"
        @confirm-add="(payload) => $emit('confirm-add', payload)"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'RuleBuilderNode',
  props: {
    node: Object,
    level: Number,
    parentAmount: [Number, String],
    usedFields: Array,
    fields: Array,
    currentFieldId: [Number, String]
  },
  data() {
    return {
      collapsed: false
    }
  },
  computed: {
    placeholderText() {
      return this.parentAmount ? `৳${this.parentAmount}` : 'Amount';
    },
    effectiveAmount() {
      return (this.node.amount !== null && this.node.amount !== undefined && this.node.amount !== '') 
        ? this.node.amount 
        : this.parentAmount;
    },
    availableFields() {
      // Use loose equality to handle potential string/number ID mismatches
      return this.fields.filter(f => !this.usedFields.some(usedId => usedId == f.id));
    }
  },
  methods: {
    getFieldLabel(id) {
      const field = this.fields.find(f => f.id == id);
      return field ? field.label : id;
    },
    getOptionLabel(value) {
      const field = this.fields.find(f => f.id == this.currentFieldId);
      if (field && field.options) {
        const opt = field.options.find(o => o.value == value);
        return opt ? opt.text : value;
      }
      return value;
    }
  }
}
</script>

<style scoped>
/* Same palette as the parent RuleBuilder — the #BDDBCE hairline, teal type and
   the #CC5057 outline on anything destructive. */
.rule-builder-node {
    font-family: 'Poppins', sans-serif;
}

/* The row itself draws nothing: in the design only the amount field is
   outlined, with the option label and the actions sitting loose beside it. */
.node-row {
    margin-bottom: 12px;
}

.value-label {
    flex: 0 0 auto;
    min-width: 55px;
    margin-right: 15px;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: -0.02em;
    color: #01524B;
}

.amount-wrapper {
    flex: 1 1 auto;
    min-width: 0;
}

.node-actions {
    flex: 0 0 auto;
    margin-left: 10px;
}

.tiny-hint {
    font-size: 10px;
    font-weight: 500;
    color: #8F8F8F;
    margin-top: 2px;
}

.ctc-dashed-left {
    border-left: 1px dashed #BDDBCE;
    margin-top: 12px;
    margin-left: 5px;
    padding-left: 15px;
}

/* Names the attribute the nested rows belong to (Shift, Other …). */
.rb-field-caption {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: #8F8F8F;
    margin-bottom: 10px;
}

.rule-builder-node .form-control {
    height: 27px;
    border: 1px solid #BDDBCE;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: #01524B;
    background-color: #ffffff;
    box-shadow: none;
}

.rule-builder-node .form-control::placeholder {
    color: #8F8F8F;
    font-size: 11px;
    font-weight: 600;
}

.rule-builder-node .form-control:focus {
    border-color: #01524B;
    box-shadow: 0 0 0 2px rgba(1, 82, 75, 0.12);
    outline: none;
}

.rb-btn-sub {
    background-color: #ffffff;
    border: 1px solid #01524B;
    border-radius: 4px;
    color: #01524B;
    font-size: 12px;
    font-weight: 500;
    line-height: 15px;
    letter-spacing: -0.04em;
    padding: 5px 10px;
    height: 27px;
    white-space: nowrap;
}
.rb-btn-sub:hover {
    background-color: #01524B;
    color: #ffffff;
}

/* 32×27 outlined square, as the design draws the row delete. */
.rb-btn-delete {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 27px;
    padding: 0;
    background-color: #ffffff;
    border: 1px solid #CC5057;
    border-radius: 3px;
    color: #CC5057;
    font-size: 12px;
}
.rb-btn-delete:hover {
    background-color: #CC5057;
    color: #ffffff;
}

.rb-btn-caret {
    background: none;
    border: none;
    color: #01524B;
    font-size: 11px;
    padding: 0 4px;
    margin-left: 6px;
}

.rb-btn-add {
    background-color: #01524B;
    border: 1px solid #01524B;
    border-radius: 4px;
    color: #ffffff;
    font-size: 12px;
    font-weight: 500;
    line-height: 15px;
    letter-spacing: -0.04em;
    padding: 5px 10px;
    height: 27px;
    text-transform: capitalize;
    white-space: nowrap;
}
.rb-btn-add:hover {
    background-color: #013e36;
    border-color: #013e36;
    color: #ffffff;
}

.rb-btn-cancel {
    background: none;
    border: none;
    color: #747474;
    font-size: 12px;
    font-weight: 500;
    padding: 5px 8px;
    text-transform: capitalize;
}
.rb-btn-cancel:hover {
    color: #01524B;
}

/* Child Selector Box */
.child-selector-box {
    border: 1px solid #BDDBCE;
    border-radius: 4px;
    padding: 12px;
    background-color: #F2FAF5;
    margin-bottom: 12px;
}

.rb-selector-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: #01524B;
    margin-bottom: 6px;
}

/* One teal chevron rather than the stacked up/down pair — same treatment the
   modal's own selects get, so every dropdown in the form reads the same. */
.rule-builder-node select {
    appearance: none !important;
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='%2301524B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpath d='M4 6l4 4 4-4'/%3e%3c/svg%3e") !important;
    background-repeat: no-repeat !important;
    background-position: right 8px center !important;
    background-size: 12px 12px !important;
    padding-right: 26px !important;
}

/* The amount field carries no chevron — it is typed, not stepped, so the native
   spinner is suppressed and nothing is drawn in its place. */
.rule-builder-node input[type="number"]::-webkit-outer-spin-button,
.rule-builder-node input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none !important;
    margin: 0 !important;
}

.rule-builder-node input[type="number"] {
    -moz-appearance: textfield !important;
    appearance: textfield !important;
    background-image: none !important;
}
</style>
